/**
 * HubSpot Form Events — Tracker
 *
 * Listens for window.postMessage events dispatched by embedded HubSpot
 * forms (hsFormCallback / onFormSubmitted), looks the form's GUID up in
 * the Registry, and pushes a unique per-form dataLayer event so GTM can
 * route each submission to its own Google Ads / GA4 conversion action.
 *
 * Payload shape (registered form):
 *   {
 *     event: 'hsform_trial_submission',
 *     hubspot_form_id: '<guid>',
 *     hubspot_form_name: 'Marketing Center Free Trial',
 *     form_data: { firstname, lastname, ... },          // allowlisted
 *     enhanced_conversion_data: {                        // when enabled
 *       email: 'jane@acme.com',
 *       phone_number: '+12345551234',                    // E.164
 *     },
 *   }
 *
 * Unregistered forms emit `hsform_unmapped` with the GUID so new forms
 * don't go silent.
 *
 * Debug: ?thryvDebug=hsFormEvents (via helpers.shouldDebug)
 *
 * See README.md in this folder for usage and the GTM side.
 */

/**
 * Default HubSpot field names for Enhanced Conversions data. Used when
 * the registry entry sets `enhancedConversions: true`. Overridable
 * per-form by passing an object (merges with these defaults).
 */
const DEFAULT_EC_FIELDS = { email: "email", phone: "phone" };

const tracker = {
  DEFAULT_EC_FIELDS,

  init() {
    if (window.__thryvHSFormEventsInitialized) return;
    window.__thryvHSFormEventsInitialized = true;

    window.addEventListener("message", (event) => this.handleMessage(event));
    this.log("initialized");
  },

  handleMessage(event) {
    if (!event || !event.data) return;
    if (event.data.type !== "hsFormCallback") return;
    if (event.data.eventName !== "onFormSubmitted") return;

    const guid = event.data.id;
    if (!guid) return;

    const registry = window.ThryvHSFormEvents.Registry || {};
    const entry = registry[guid];

    if (!entry) {
      this.pushUnmapped(guid);
      return;
    }

    const values = this.extractSubmissionValues(event.data);
    const payload = this.buildPayload(guid, values, entry);

    (window.dataLayer = window.dataLayer || []).push(payload);
    this.logPush("fire " + entry.eventName, guid, entry, values, payload);
  },

  pushUnmapped(guid) {
    const payload = Object.assign(
      {
        event: "hsform_unmapped",
        hubspot_form_id: guid,
      },
      this.getContext(),
    );

    (window.dataLayer = window.dataLayer || []).push(payload);
    this.logPush("unmapped", guid, null, null, payload);
  },

  buildPayload(guid, values, entry) {
    const payload = Object.assign(
      {
        event: entry.eventName,
        hubspot_form_id: guid,
        hubspot_form_name: entry.formName,
      },
      this.getContext(),
    );

    const formData = this.buildFormData(values, entry.allowedFields);
    if (formData) payload.form_data = formData;

    const ec = this.buildEnhancedConversionData(values, entry.enhancedConversions);
    if (ec) payload.enhanced_conversion_data = ec;

    return payload;
  },

  /**
   * Per-submission page context added to every payload. `page_path` comes
   * straight from the browser so it's always current; `site_country` reads
   * the theme's `window.thryvData.thryv_country` (US / AU / CA / NZ).
   */
  getContext() {
    let country = "";
    if (window.thryvData && typeof window.thryvData.thryv_country === "string") {
      country = window.thryvData.thryv_country;
    }
    return {
      page_path: (window.location && window.location.pathname) || "",
      site_country: country,
    };
  },

  buildFormData(values, allowedFields) {
    if (!Array.isArray(allowedFields) || allowedFields.length === 0) return null;

    const filtered = {};
    for (let i = 0; i < allowedFields.length; i++) {
      const key = allowedFields[i];
      if (values[key] !== undefined && values[key] !== "") {
        filtered[key] = values[key];
      }
    }
    return Object.keys(filtered).length > 0 ? filtered : null;
  },

  /**
   * Pulls email + phone from the submission using the field names declared
   * in the registry's `enhancedConversions` config.
   *   true             → use DEFAULT_EC_FIELDS
   *   { phone: 'x' }   → partial override, rest fall back to defaults
   *   omitted / false  → return null (no EC data)
   */
  buildEnhancedConversionData(values, config) {
    if (!config) return null;

    const fields =
      config === true ? this.DEFAULT_EC_FIELDS : Object.assign({}, this.DEFAULT_EC_FIELDS, config);

    const ec = {};

    if (fields.email) {
      const rawEmail = values[fields.email];
      if (rawEmail && typeof rawEmail === "string" && rawEmail.indexOf("@") !== -1) {
        ec.email = rawEmail.trim();
      }
    }

    if (fields.phone) {
      const rawPhone = values[fields.phone];
      if (rawPhone && typeof rawPhone === "string") {
        const e164 = this.normalizePhoneE164(rawPhone);
        if (e164) ec.phone_number = e164;
      }
    }

    return Object.keys(ec).length > 0 ? ec : null;
  },

  /**
   * HubSpot sends submission values in two shapes depending on form config:
   *   - data.submissionValues: object keyed by field name (preferred)
   *   - data: array of { name, value } (older callback shape)
   */
  extractSubmissionValues(eventData) {
    if (!eventData || !eventData.data) return {};

    if (eventData.data.submissionValues && typeof eventData.data.submissionValues === "object") {
      return eventData.data.submissionValues;
    }

    if (Array.isArray(eventData.data)) {
      const out = {};
      eventData.data.forEach((f) => {
        if (f && f.name) out[f.name] = f.value;
      });
      return out;
    }

    return {};
  },

  /**
   * Normalize a US phone string to E.164.
   *   (123) 123-4567      → +11231234567
   *   123-123-4567        → +11231234567
   *   1231234567          → +11231234567
   *   +1 123 123 4567     → +11231234567
   *   unrecognized input  → ''
   */
  normalizePhoneE164(raw) {
    if (!raw || typeof raw !== "string") return "";

    const s = raw.trim();
    if (s === "") return "";

    if (s.charAt(0) === "+") {
      const rest = s.slice(1).replace(/\D/g, "");
      return rest ? "+" + rest : "";
    }

    const digits = s.replace(/\D/g, "");
    if (digits.length === 10) return "+1" + digits;
    if (digits.length === 11 && digits.charAt(0) === "1") return "+" + digits;

    return "";
  },

  log(...args) {
    if (typeof helpers === "undefined" || !helpers.shouldDebug("hsFormEvents")) return;
    console.log("[HSFormEvents.Tracker]", ...args);
  },

  /**
   * Structured debug log for dataLayer pushes. Renders a collapsed group in
   * the console so one submission = one expandable block.
   *
   * `payload` IS the dataLayer entry — after the push, GTM mutates it in
   * place to add `gtm.uniqueEventId`, which is a useful side-signal that
   * GTM received it.
   */
  logPush(label, guid, entry, values, payload) {
    if (typeof helpers === "undefined" || !helpers.shouldDebug("hsFormEvents")) return;

    console.groupCollapsed("[HSFormEvents.Tracker] " + label + "  (" + guid + ")");
    if (entry) console.log("[HSFormEvents.Tracker] registry entry:", entry);
    if (values) console.log("[HSFormEvents.Tracker] submissionValues:", values);
    console.log("[HSFormEvents.Tracker] payload:", payload);
    console.groupEnd();
  },
};

export default tracker;
